// recova GmbH – Öffentliches Live-Tracking: Timeline, Terminvorschlag-Antwort, Gutschein, Rechnung.
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { API, fileUrl, fmtErr } from "@/lib/api";
import { Recycle, CheckCircle2, Circle, Truck, PackageSearch, CalendarCheck, CalendarClock, ClipboardList, PartyPopper, AlertTriangle, Download } from "lucide-react";

const STEPS = [
  { key: "angefragt", label: "Angefragt", icon: ClipboardList },
  { key: "terminvorschlag", label: "Terminvorschlag", icon: CalendarClock },
  { key: "termin_vereinbart", label: "Termin vereinbart", icon: CalendarCheck },
  { key: "techniker_unterwegs", label: "Techniker unterwegs", icon: Truck },
  { key: "ersatzteil", label: "Ersatzteil disponiert", icon: PackageSearch },
  { key: "abgeschlossen", label: "Erfolgreich abgeschlossen", icon: PartyPopper },
];

const stepIndex = (status) => {
  if (["teil_reserviert_halle", "wartet_auf_lieferant", "bereit_fuer_zweittermin"].includes(status)) return 4;
  if (status === "vor_ort") return 3;
  const i = STEPS.findIndex((s) => s.key === status);
  return i;
};

export default function TrackingPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [respondBusy, setRespondBusy] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineNote, setDeclineNote] = useState("");

  const load = () =>
    API.get(`/public/track/${token}`).then((r) => setData(r.data)).catch((e) => setError(fmtErr(e)));

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [token]);

  const respond = async (accept) => {
    setRespondBusy(true);
    try {
      await API.post(`/public/track/${token}/respond`, { accept, note: accept ? null : declineNote || null });
      setDeclineOpen(false);
      await load();
    } catch (e) {
      setError(null);
      alert(fmtErr(e));
    } finally {
      setRespondBusy(false);
    }
  };

  if (error)
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#f8faf9] px-4 text-center">
        <AlertTriangle className="h-12 w-12 text-[#e76f51]" />
        <p className="mt-4 text-lg font-semibold text-[#0f172a]" data-testid="tracking-error">{error}</p>
        <Link to="/" className="mt-4 text-sm text-[#028090] hover:underline">Zur Startseite</Link>
      </div>
    );
  if (!data)
    return <div className="flex min-h-screen items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-[#1b4332] border-t-transparent" /></div>;

  const current = stepIndex(data.status);
  const isTotal = data.status === "totalschaden";

  return (
    <div className="min-h-screen bg-[#f8faf9]">
      <header className="glass-header">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2" data-testid="tracking-logo">
            <img src="/logo.png" alt="recova Logo" className="h-9 w-9 rounded-full object-cover" data-testid="logo" />
            <span className="font-heading text-lg font-extrabold text-[#1b4332]">recova</span>
          </Link>
          <span className="font-mono2 text-xs uppercase tracking-widest text-[#028090]">Live-Status</span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10">
        <p className="text-sm text-slate-500">Auftrag</p>
        <h1 className="font-heading text-3xl font-extrabold text-[#0f172a]" data-testid="tracking-order-number">{data.order_number}</h1>
        <p className="mt-1 text-sm text-slate-500" data-testid="tracking-appliance">
          {data.appliance?.brand} {data.appliance?.type_label} {data.appliance?.model && `· ${data.appliance.model}`} {data.appliance?.e_number && `· E-Nr. ${data.appliance.e_number}`}
        </p>

        <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#1b4332]/25 bg-[#e8f0ec] px-4 py-1.5 text-sm font-semibold text-[#1b4332]" data-testid="tracking-current-status">
          {data.status_label}
        </div>

        {data.status === "terminvorschlag" && data.proposed && (
          <div className="mt-6 rounded-2xl border-2 border-[#028090] bg-cyan-50 p-6" data-testid="proposal-card">
            <p className="font-mono2 text-xs uppercase tracking-widest text-[#028090]">Unser Terminvorschlag</p>
            <p className="font-heading mt-2 text-2xl font-extrabold text-[#0f172a]" data-testid="proposal-datetime">
              {data.proposed.date} · {data.proposed.window}
            </p>
            <p className="mt-1 text-sm text-slate-600">Ihr Techniker: {data.proposed.technician_name}</p>
            {!declineOpen ? (
              <div className="mt-5 flex flex-wrap gap-3">
                <button className="btn-primary" onClick={() => respond(true)} disabled={respondBusy} data-testid="proposal-accept">
                  {respondBusy ? "Bitte warten…" : "Termin bestätigen"}
                </button>
                <button className="btn-secondary" onClick={() => setDeclineOpen(true)} disabled={respondBusy} data-testid="proposal-decline">
                  Passt nicht – anderen Termin anfragen
                </button>
              </div>
            ) : (
              <div className="mt-5 space-y-3">
                <textarea
                  className="w-full rounded-lg border border-slate-300 p-3 text-sm"
                  rows={2}
                  placeholder="Optional: Wann würde es Ihnen besser passen?"
                  value={declineNote}
                  onChange={(e) => setDeclineNote(e.target.value)}
                  data-testid="decline-note"
                />
                <div className="flex gap-3">
                  <button className="btn-secondary" onClick={() => respond(false)} disabled={respondBusy} data-testid="decline-submit">
                    Absenden
                  </button>
                  <button className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600" onClick={() => setDeclineOpen(false)} data-testid="decline-cancel">
                    Zurück
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {isTotal ? (
          <div className="mt-8 rounded-2xl border border-red-200 bg-red-50 p-6" data-testid="tracking-totalschaden">
            <h3 className="font-heading text-lg font-semibold text-red-700">Totalschaden / Reparatur abgelehnt</h3>
            <p className="mt-1 text-sm text-red-600">Ihr Gerät konnte leider nicht wirtschaftlich repariert werden. Wir übernehmen die fachgerechte Entsorgung.</p>
            {data.voucher && (
              <div className="mt-4 rounded-xl border border-[#1b4332]/20 bg-white p-4">
                <p className="text-xs uppercase tracking-widest text-[#028090]">recova Altgeräte-Garantie</p>
                <p className="mt-1 text-sm text-slate-600">Ihr Gutscheincode ({data.voucher.discount}):</p>
                <p className="font-mono2 text-xl font-bold text-[#1b4332]" data-testid="tracking-voucher-code">{data.voucher.code}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8" data-testid="tracking-timeline">
            {STEPS.map((s, i) => {
              const done = i < current || data.status === "abgeschlossen";
              const active = i === current && data.status !== "abgeschlossen";
              const Icon = s.icon;
              return (
                <div key={s.key} className="flex gap-4" data-testid={`timeline-step-${s.key}`}>
                  <div className="flex flex-col items-center">
                    <div className={`flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors ${
                      done ? "border-[#1b4332] bg-[#1b4332] text-white" : active ? "border-[#028090] bg-cyan-50 text-[#028090]" : "border-slate-200 bg-white text-slate-300"}`}>
                      {done ? <CheckCircle2 className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                    </div>
                    {i < STEPS.length - 1 && <div className={`h-10 w-0.5 ${i < current ? "bg-[#1b4332]" : "bg-slate-200"}`} />}
                  </div>
                  <div className="pb-8 pt-2">
                    <p className={`text-sm font-semibold ${done || active ? "text-[#0f172a]" : "text-slate-400"}`}>{s.label}</p>
                    {active && <p className="text-xs text-[#028090]">Aktueller Status</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {data.scheduled_date && (
            <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="tracking-appointment">
              <p className="text-xs uppercase tracking-widest text-slate-400">Termin</p>
              <p className="mt-1 font-semibold text-[#0f172a]">{data.scheduled_date} · {data.time_window || "wird bestätigt"}</p>
              {data.technician && <p className="mt-1 text-sm text-slate-500">Ihr Techniker: {data.technician}</p>}
            </div>
          )}
          {data.invoice_url && (
            <a href={fileUrl(data.invoice_url)} target="_blank" rel="noreferrer" className="rounded-xl border border-slate-200 bg-white p-5 transition-shadow hover:shadow-md" data-testid="tracking-invoice">
              <p className="text-xs uppercase tracking-widest text-slate-400">Dokumente</p>
              <p className="mt-1 flex items-center gap-2 font-semibold text-[#028090]"><Download className="h-4 w-4" /> Rechnung / Quittung (PDF)</p>
            </a>
          )}
        </div>

        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="tracking-history">
          <p className="mb-3 text-xs uppercase tracking-widest text-slate-400">Verlauf</p>
          <ul className="space-y-2">
            {[...(data.history || [])].reverse().map((h, i) => (
              <li key={i} className="flex items-baseline justify-between gap-4 text-sm">
                <span className="font-medium text-[#0f172a]">{h.label}</span>
                <span className="text-xs text-slate-400">{new Date(h.at).toLocaleString("de-DE")}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="mt-8 text-center text-xs text-slate-400">Diese Seite aktualisiert sich automatisch alle 15 Sekunden. Fragen? 02271 - 5059265 · info@recova-service.de</p>
      </main>
    </div>
  );
}
